import { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';

const TESTIMONIALS = [
  {
    id: 1,
    name: 'María G.',
    role: 'Freelancer',
    avatar: 'MG',
    color: 'bg-pink-500',
    content: 'Antes vivía al día sin saber a dónde se iba mi plata. Con Kuadra2 pude identificar que gastaba más de $200k mensuales en cafeterías sin darme cuenta. Ahora tengo control total.',
    metric: '$200k',
    metricLabel: 'ahorradas al mes',
    rating: 5,
  },
  {
    id: 2,
    name: 'Carlos y Ana',
    role: 'Pareja',
    avatar: 'CA',
    color: 'bg-blue-500',
    content: 'Dejamos de pelearnos por quién pagaba qué. El modo pareja nos salvó la relación. Ahora sabemos exactamente cuánto aporta cada uno y tenemos metas juntos.',
    metric: '0',
    metricLabel: 'peleas por dinero',
    rating: 5,
  },
  {
    id: 3,
    name: 'Diego R.',
    role: 'Padre de familia',
    avatar: 'DR',
    color: 'bg-emerald-500',
    content: 'Tengo 3 hijos y antes no sabía cuánto gastaba cada uno. Ahora asigno presupuestos por persona y enseño a mis hijos a administrar su mesada.',
    metric: '30%',
    metricLabel: 'más ahorro familiar',
    rating: 5,
  },
  {
    id: 4,
    name: 'Valentina S.',
    role: 'Emprendedora',
    avatar: 'VS',
    color: 'bg-purple-500',
    content: 'Separar mis finanzas personales de mi negocio era un caos. Con el modo negocio de Kuadra2 tengo todo organizado y sé exactamente cuánto gana mi empresa.',
    metric: '2h',
    metricLabel: 'ahorradas semanal',
    rating: 5,
  },
  {
    id: 5,
    name: 'Javier M.',
    role: 'Ingeniero',
    avatar: 'JM',
    color: 'bg-amber-500',
    content: 'El escáner de boletas es mágico. Tomo foto a mis compras y listo, no tengo que escribir nada. Me ha ahorrado horas de trabajo manual.',
    metric: '5min',
    metricLabel: 'para registrar todo',
    rating: 5,
  },
];

export default function TestimonialsSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef();

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  const next = () => {
    setActiveIndex((prev) => (prev + 1) % TESTIMONIALS.length);
  };

  const prev = () => {
    setActiveIndex((prev) => (prev - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
  };

  const current = TESTIMONIALS[activeIndex];

  return (
    <section
      ref={sectionRef}
      className="py-20 px-4 bg-gradient-to-b from-white to-gray-50 dark:from-gray-950 dark:to-gray-900/50"
    >
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-3 py-1 rounded-full bg-brand-100 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 text-xs font-semibold mb-4">
            TESTIMONIOS
          </span>
          <h2 className="text-3xl sm:text-4xl font-display font-bold text-gray-900 dark:text-white mb-4">
            Lo que dicen nuestros usuarios
          </h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-xl mx-auto">
            Miles de personas ya ordenaron sus finanzas con Kuadra2. 
            Estas son algunas de sus historias.
          </p>
        </div>

        {/* Main testimonial card */}
        <div
          className={`transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="relative bg-white dark:bg-gray-900 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-800 p-8 sm:p-10">
            {/* Quote icon */}
            <div className="absolute -top-4 left-8 w-8 h-8 rounded-full bg-brand-500 flex items-center justify-center">
              <Quote size={16} className="text-white" />
            </div>

            <div className="grid md:grid-cols-[1fr,auto] gap-8 items-center">
              {/* Content */}
              <div>
                {/* Stars */}
                <div className="flex gap-1 mb-4">
                  {[...Array(current.rating)].map((_, i) => (
                    <svg
                      key={i}
                      className="w-5 h-5 text-amber-400 fill-amber-400"
                      viewBox="0 0 20 20"
                    >
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>

                {/* Quote */}
                <blockquote className="text-lg sm:text-xl text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                  "{current.content}"
                </blockquote>

                {/* Author */}
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full ${current.color} flex items-center justify-center text-white font-bold`}>
                    {current.avatar}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 dark:text-white">{current.name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{current.role}</div>
                  </div>
                </div>
              </div>

              {/* Metric */}
              <div className="text-center md:text-right">
                <div className="inline-flex flex-col items-center md:items-end px-6 py-4 rounded-2xl bg-gradient-to-br from-brand-50 to-accent-50 dark:from-brand-500/10 dark:to-accent-500/10">
                  <span className="text-4xl font-extrabold text-gradient">{current.metric}</span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">{current.metricLabel}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6">
            {/* Dots */}
            <div className="flex gap-2">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === activeIndex
                      ? 'w-6 bg-brand-500'
                      : 'w-2 bg-gray-300 dark:bg-gray-700 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>

            {/* Arrows */}
            <div className="flex gap-2">
              <button
                onClick={prev}
                className="p-2 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <ChevronLeft size={20} className="text-gray-600 dark:text-gray-300" />
              </button>
              <button
                onClick={next}
                className="p-2 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <ChevronRight size={20} className="text-gray-600 dark:text-gray-300" />
              </button>
            </div>
          </div>
        </div>

        {/* Trust badges */}
        <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-800">
          <p className="text-center text-sm text-gray-500 dark:text-gray-400 mb-6">
            Usado por personas de todo Latinoamérica
          </p>
          <div className="flex flex-wrap justify-center gap-8 opacity-50">
            {['🇨🇱 Chile', '🇲🇽 México', '🇦🇷 Argentina', '🇨🇴 Colombia', '🇵🇪 Perú', '🇪🇸 España'].map(
              (country) => (
                <span
                  key={country}
                  className="text-2xl grayscale hover:grayscale-0 transition-all cursor-default"
                  title={country}
                >
                  {country.split(' ')[0]}
                </span>
              )
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
